import { FormEvent, ReactElement, useCallback, useEffect, useState } from 'react';

import { ConsumerFlow } from '@breathelife/types';
import { Icon, ResponsiveProps, withResponsive } from '@breathelife/ui-components';
import { NodeIdToAnswerPathMap, QuestionnaireBlueprint, QuestionnaireEngine } from '@zinnia/form-engine-sdk';

import { FormView } from '../../Components/FormView/FormView';
import { useStep } from '../../Hooks/useStep';
import { CenteredLayout } from '../../Layouts/Centered/Layout';
import { PlanFinderLayout } from '../../Layouts/PlanFinder/Layout';
import { TransactionFlowLayout } from '../../Layouts/TransactionFlow/Layout';
import { shortLocale } from '../../Localization/Localizer';
import { StepProps } from '../../Models/Question';

export type Props = {
  progress: number;
  displayErrors: boolean;
  popup: ReactElement | null;
  isLoading: boolean;
  showLoadingPage: boolean;
  onBackButtonClick: () => void;
  layout: ConsumerFlow.Layout;
  landingStepsIds: string[];
  questionnaireEngine: QuestionnaireEngine;
  nodeIdToAnswerPathMap: NodeIdToAnswerPathMap;
  blueprint: QuestionnaireBlueprint;
  setShouldRefreshApplicationPremium: (shouldRefreshApplicationPremium: boolean) => void;
} & ResponsiveProps &
  StepProps;

export function StepPageBase(props: Props): ReactElement | null {
  const {
    questionnaireEngine,
    displayErrors = false,
    step,
    progress,
    popup,
    showLoadingPage,
    onBackButtonClick,
    layout,
    landingStepsIds,
    onAnswerComplete,
    onInfoIconClick,
    onError,
    submitAnswers,
  } = props;

  const {
    setLocalVersionedAnswers,
    setSubmitFormRequested,
    renderingQuestionnaire,
    renderingStep,
    onSubmit: onSubmitHook,
    onAnswerChange,
  } = useStep(questionnaireEngine, displayErrors, submitAnswers);

  const [isNextBtnLoading, setIsNextBtnLoading] = useState(false);

  useEffect(() => {
    setIsNextBtnLoading(false);
  }, [renderingStep.id]);

  useEffect(() => {
    const scrollToTopOnLoad = layout === ConsumerFlow.Layout.planFinder;

    if (!showLoadingPage && scrollToTopOnLoad) {
      document.getElementById('root')?.scrollIntoView();
    }
  }, [showLoadingPage, layout]);

  const onSubmit = useCallback(() => {
    if (isNextBtnLoading) return;

    setIsNextBtnLoading(true);

    if (onSubmitHook()) return;

    setIsNextBtnLoading(false);
  }, [isNextBtnLoading, onSubmitHook]);

  const layoutLookup = {
    [ConsumerFlow.Layout.planFinder]: PlanFinderLayout,
    [ConsumerFlow.Layout.transactionFlow]: TransactionFlowLayout,
    [ConsumerFlow.Layout.centered]: CenteredLayout,
  };

  const FlowLayout = layoutLookup[layout];
  if (!FlowLayout) throw new Error('Layout not found');

  const onUseNeedsAnalysis = useCallback(
    (useNeedsAnalysis: boolean): void => {
      setLocalVersionedAnswers((prevVersionedAnswers) => {
        return prevVersionedAnswers;
      });

      if (useNeedsAnalysis) {
        setSubmitFormRequested(true);
      }
    },
    [questionnaireEngine, setLocalVersionedAnswers, setSubmitFormRequested],
  );

  const language = shortLocale();

  const formProps = {
    renderingQuestionnaire,
    renderingStepId: renderingStep.id,
    onSubmit: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit();
    },
    onInfoIconClick,
    language,
    onAnswerComplete,
    onBackButtonClick,
    landingStepsIds,
    onError,
    onAnswerChange,
    nextStepButtonText: renderingStep.nextStepButtonText,
    onUseNeedsAnalysis,
    isNextBtnLoading,
  };

  return (
    // TODO: Fix typing/instantiation issue
    // @ts-ignore JSX element type 'FlowLayout' does not have any construct or call signatures
    <FlowLayout
      title={renderingStep.title}
      subtitle={renderingStep.text}
      questionId={step.id}
      progress={progress}
      hideHeaderOnDesktop={false}
      popup={popup}
      isLoading={showLoadingPage}
      onBackButtonClick={onBackButtonClick}
      useEngine={true}
      isFullWidthLayout={false}
      offsetContent={step.iconName ? <Icon name={step.iconName} size='60px' /> : undefined}
    >
      <FormView {...formProps} />
    </FlowLayout>
  );
}

export const StepPage = withResponsive(StepPageBase);
